import { GIFT_LEVEL_MEIDOU_MAP, TABS, TabType } from '@/constants/subscription';
import classNames from 'classnames';
import React, { useState, useEffect, useMemo } from 'react';
import styles from './index.module.css';
import { useAtom } from 'jotai';
import { curTabAtom, giftLevelAtom, showTabsAtom } from '@/store';
import { checkIsDefaultSecondTierMeidou } from '@/utils/abtest';
import { track } from '@/utils/common';
import { getSkuType, getUrlParam } from '@/utils';
import { isTeamSubscription, SubscribeType, subscribeType } from '@/constants';
import { getIncomeSource } from '@/utils/vip';

export interface TabItem {
  name: string;
  key: string;
  tag?: string;
  icon?: React.ReactNode;
}

interface TabsProps {
  /** 自定义 tabs 数据，不传则使用默认的会员/美豆 tabs */
  tabs?: TabItem[];
  /** 受控模式：当前激活的 tab key */
  value?: string;
  /** 受控模式：tab 切换回调 */
  onChange?: (key: string) => void;
  /** 尺寸：small（小，34/26px）| large（默认，48/40px） */
  size?: 'small' | 'large';
}

const Tabs = ({
  tabs: customTabs,
  value: customValue,
  onChange: customOnChange,
  size = 'small',
}: TabsProps = {}) => {
  const [curTab, setCurTab] = useAtom(curTabAtom);
  const [giftLevel, setGiftLevel] = useAtom(giftLevelAtom);
  const [, setShowTabs] = useAtom(showTabsAtom);
  /** 首帧关闭 transition，避免挂载时滑块从 0 位「动画」到当前 tab */
  const [tabIndicatorMotion, setTabIndicatorMotion] = useState(false);

  const activeKey = customValue !== undefined ? customValue : curTab;
  const isSm = size === 'small';

  const tabList = useMemo(() => {
    if (customTabs) {
      return customTabs;
    }
    let list: typeof TABS = [];
    if (!isTeamSubscription) {
      // 个人版
      if (subscribeType === SubscribeType.MEIDOU) {
        // 美豆场景，展示开通会员、开通高级会员和充值美豆tab
        list = TABS;
      } else if (subscribeType === SubscribeType.SUBSCRIPTION) {
        // 会员场景，展示开通会员、开通高级会员
        list = TABS.filter((v) => v.key === TabType.SUB || v.key === TabType.PRO_SUB);
      } else {
        list = [];
      }
    } else {
      // 团队版
      // 美豆场景，展示开通会员和充值美豆tab
      if (subscribeType === SubscribeType.MEIDOU) {
        list = TABS.filter((v) => v.key === TabType.SUB || v.key === TabType.MEIDOU);
      } else {
        list = [];
      }
    }

    if (getIncomeSource() !== 'MTXX-WEB') {
      // PC 内嵌等场景不开放 Pro 购买入口，仅过滤高级会员 tab
      // 批处理，不展示tabs
      list = getUrlParam('type') === 'svip' ? [] : list.filter((v) => v.key !== TabType.PRO_SUB);
    }

    list = list.map((tab) => {
      if (isTeamSubscription && tab.key === TabType.SUB) {
        return { ...tab, name: '团队版' };
      }
      return tab;
    });

    // 如果只有一个则不展示
    list = list.length > 1 ? list : [];

    setShowTabs(!!list.length);

    return list;
  }, [isTeamSubscription, subscribeType, giftLevel]);

  useEffect(() => {
    const id = requestAnimationFrame(() => setTabIndicatorMotion(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const activeTabIndex = Math.max(
    0,
    tabList.findIndex((t) => t.key === activeKey)
  );

  const handleTabClick = (key: string) => {
    if (customOnChange) {
      customOnChange(key);
    } else {
      setCurTab(key as TabType);
      // 切换到高级会员 tab 时，根据实验设置美豆默认第一档或第二档
      if (key === TabType.PRO_SUB && getUrlParam('isOnlyShowScanUI') !== '1') {
        setGiftLevel(checkIsDefaultSecondTierMeidou() ? 2 : 1);
      }
      track({
        event_id: 'vip_page_click',
        params: {
          sku_type: getSkuType(),
          class: key === TabType.MEIDOU ? 'buymeidou' : 'buyvip',
        },
      });
    }
  };

  if (!tabList.length) return null;

  const tabCount = tabList.length;
  const hPad = isSm ? 4 : 8;

  return (
    <div
      className={classNames(styles.subTabs, {
        [styles.subTabsSm]: isSm,
      })}
    >
      <div
        className={classNames(styles.tabIndicator, {
          [styles.tabIndicatorSm]: isSm,
          [styles.tabIndicatorNoMotion]: !tabIndicatorMotion,
        })}
        style={{
          width: `calc((100% - ${hPad}px) / ${tabCount})`,
          transform: `translateX(${activeTabIndex * 100}%)`,
        }}
        aria-hidden
      />
      {tabList.map((v) => {
        return (
          <div
            className={classNames(styles.subTabsItem, {
              [styles.subTabsItemSm]: isSm,
              [styles.active]: activeKey === v.key,
            })}
            onClick={() => handleTabClick(v.key)}
            key={v.key}
          >
            <span>{v.name}</span>
            {v.tag ? <span className={styles.tag}>{v.tag}</span> : null}
          </div>
        );
      })}
    </div>
  );
};

export default React.memo(Tabs);
